import { useEffect, useRef, useState } from 'react'
import type { CSSProperties, ReactNode } from 'react'
import { PrincipalSearch } from './PrincipalSearch'
import type { PrincipalSearchItem, PrincipalType } from '../lib/types'

export interface AddPrincipalDialogLabels {
  title: string
  subtitle: string
  typeLabel: string
  searchUsersLabel: string
  searchGroupsLabel: string
  clearSelection: string
  cancel: string
  add: string
}

export interface AddPrincipalDialogProps {
  labels: AddPrincipalDialogLabels
  /** Id for the User/Group select — must be unique per dialog on the page. */
  typeSelectId: string
  /** Principals already granted; hidden from the search results. */
  excludeIds: string[]
  labelStyle: CSSProperties
  /** Avatar shown next to the selected principal (Global Access uses the shared
      teal <Avatar>). */
  renderAvatar: (principal: PrincipalSearchItem) => ReactNode
  /** Extra controls rendered under the search field (e.g. a permission select). */
  extraFields?: ReactNode
  onCancel: () => void
  onAdd: (principal: PrincipalSearchItem) => void
}

/**
 * Add-a-principal modal used by the Global (Studio) Access card. A native <dialog open>
 * rather than a div with role="dialog", so the dialog role and modal semantics come
 * from the platform.
 */
export function AddPrincipalDialog({
  labels,
  typeSelectId,
  excludeIds,
  labelStyle,
  renderAvatar,
  extraFields,
  onCancel,
  onAdd,
}: Readonly<AddPrincipalDialogProps>) {
  const [type, setType] = useState<PrincipalType>('User')
  const [principal, setPrincipal] = useState<PrincipalSearchItem | null>(null)
  const backdropRef = useRef<HTMLDivElement>(null)

  // Dismiss on backdrop click. Registered on the node rather than as a JSX onClick
  // so the backdrop stays a purely presentational element with no ARIA obligations
  // (keyboard users dismiss via the Cancel button).
  useEffect(() => {
    const backdrop = backdropRef.current
    function handleBackdropClick(e: MouseEvent) {
      if (e.target === backdrop) onCancel()
    }
    document.addEventListener('click', handleBackdropClick)
    return () => document.removeEventListener('click', handleBackdropClick)
  }, [onCancel])

  return (
    <div ref={backdropRef} className="modal-backdrop">
      <dialog open aria-modal="true" aria-label={labels.title} className="modal modal-overflow-visible" style={{ width: 440 }}>
        <div className="modal-header">
          <h3>{labels.title}</h3>
        </div>
        <div className="modal-body">
          <p>{labels.subtitle}</p>
          <div style={{ marginBottom: 16 }}>
            <label htmlFor={typeSelectId} style={labelStyle}>{labels.typeLabel}</label>
            <select
              id={typeSelectId}
              value={type}
              onChange={(e) => { setType(e.target.value as PrincipalType); setPrincipal(null) }}
              className="form-select"
            >
              <option value="User">User</option>
              <option value="Group">Group</option>
            </select>
          </div>
          <div style={{ marginBottom: extraFields ? 16 : 0 }}>
            <label style={labelStyle}>{type === 'User' ? labels.searchUsersLabel : labels.searchGroupsLabel}</label>
            <PrincipalSearch type={type} onSelect={setPrincipal} excludeIds={excludeIds} />
            {principal && (
              <div style={{ marginTop: 8, padding: '8px 12px', background: 'var(--accent-subtle)', border: '1px solid var(--accent)', borderRadius: 6, fontSize: 13, display: 'flex', alignItems: 'center', gap: 8 }}>
                {renderAvatar(principal)}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 600 }}>{principal.principalName}</div>
                  {principal.principalEmail && <div style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>{principal.principalEmail}</div>}
                </div>
                <button type="button" aria-label={labels.clearSelection} onClick={() => setPrincipal(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-tertiary)', fontSize: 16 }}>×</button>
              </div>
            )}
          </div>
          {extraFields}
        </div>
        <div className="modal-footer">
          <button type="button" onClick={onCancel} className="btn btn-ghost">{labels.cancel}</button>
          <button
            type="button"
            onClick={() => { if (principal) onAdd(principal) }}
            disabled={!principal}
            aria-disabled={!principal}
            className="btn btn-primary"
            style={{ opacity: !principal ? 0.5 : 1 }}
          >
            {labels.add}
          </button>
        </div>
      </dialog>
    </div>
  )
}
